import {
  conversationCount,
  conversationDateRange,
  userMessageCount,
  messagesPerDay,
  timeOfDayUsage,
} from "@shared/report";

import { Validation } from "@/types/submission";
import { StudyContextData, SubmissionContextData } from "@/context/AppContext";
import { useUIContext } from "@/context/UIContext";

import { Heading } from "@/components/study/Heading";

import { DateRange } from "@/components/report/DateRange";
import { DataPoint } from "@/components/report/Datapoint";
import { ReportHeader } from "@/components/report/ReportHeader";
import { TimeOfDayUsageChart } from "@/components/report/TimeOfDayUsageChart";
import { DailyUsageChart } from "@/components/report/DailyUsageChart";

import { AppSurface } from "@shared/components/Surface";

interface ReportViewProps {
  studyContext: StudyContextData;
  submissionContext: SubmissionContextData;
}

export function reportableResult(validation: Validation | null) {
  if (validation?.status === "accepted") return validation.result;
  if (validation?.status === "rejected" && validation.overrideValidation) return validation.result;
  return null;
}

export function ReportView({ submissionContext }: ReportViewProps) {
  const { darkMode } = useUIContext();
  const result = reportableResult(submissionContext.validation);

  if (!result) return null;

  const data = result.data;

  const convoCount = conversationCount(data);
  const msgCount = userMessageCount(data);
  const dateRange = conversationDateRange(data);
  const dailyMsgs = messagesPerDay(data);
  const todUsage = timeOfDayUsage(data, 0 /* Ignore tz offset for now */);

  return (
    <div className="w-full max-w-2xl mt-12">
      <Heading />
      <p className="text-lg pb-6">
        Below is a brief overview of your AI usage based on the data you uploaded. This is meant to
        give you a general sense of your patterns — it is not a clinical assessment.
      </p>
      <AppSurface className="mb-6">
        <ReportHeader source={data.source} />
        <DateRange range={dateRange} />
        <div className="grid grid-cols-2 gap-x-4 mb-4">
          {convoCount !== null && (
            <DataPoint label="Conversations" value={convoCount.toLocaleString()} />
          )}
          <DataPoint label="Messages Sent" value={msgCount.toLocaleString()} />
        </div>
        <TimeOfDayUsageChart usage={todUsage} darkMode={darkMode} />
        <DailyUsageChart usage={dailyMsgs} darkMode={darkMode} />
      </AppSurface>
    </div>
  );
}
